import type { ReactElement } from 'react';
import React, { useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { orderBy } from 'lodash';
import { SelectField } from 'atom-components/form-fields-for-formik/select-field/select-field';
import type { ClusterFieldValue, PeriodFieldValue } from 'pages/clusters/types';
import { AreaType } from 'services/assist-api/entities/area/areas';
import { useClusterFilterAreas } from 'services/assist-api/entities/test-instance-response/filters/customer-group/area';
import { isInitialLoad } from 'utils/check-query-status';

/**
 * Select an area that the current cluster has data for
 * - disabled: if no cluster or date range
 */
export default function AreaField(): ReactElement {
  /* Get existing form state */
  const {
    values: { cluster, period },
    setFieldValue
  } = useFormikContext<PeriodFieldValue & ClusterFieldValue>();
  /* Request areas from the API */
  const areas = useClusterFilterAreas(
    {
      filters: {
        'testInstance.area.type': AreaType.Assessment,
        'testInstance.startDate[after]': period?.startDate,
        'testInstance.startDate[before]': period?.endDate,
        'testInstance.customer.customerGroupsCustomers.customerGroup': cluster
      }
    },
    { enabled: !!period?.startDate && !!period?.endDate }
  );

  /* Values from the API are unsorted, so lets sort them */
  const sortedAreas = useMemo(
    () => orderBy(areas.data?.json, ['order', 'id']) || [],
    [areas.data?.json]
  );

  /* Reset field when start or end date change */
  useEffect(
    () => () => {
      setFieldValue('area', '');
    },
    [setFieldValue, period?.startDate, period?.endDate]
  );

  return (
    <SelectField
      name="area"
      label="Area"
      options={sortedAreas}
      isLoading={isInitialLoad(areas)}
      getOptionLabel={(o): string => o.name}
      getOptionValue={(o): string => String(o.id)}
      isDisabled={!cluster || !period?.startDate || !period?.endDate}
      hideSuccessValidation
      isOnlyValue
      isRequired
    />
  );
}

export const initialArea = { area: undefined };
